import React, {memo, useCallback, useEffect, useMemo, useState} from "react";
import dayjs from "dayjs";
import {useNavigate} from "react-router-dom";
import {
  Button,
  Popper,
  Fade,
  Paper,
  Typography,
  Box,
  LinearProgress,
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Icon,
  IconButton,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import {useMutation} from "@apollo/client";
import {UPDATE_USER_METADATA} from "../../graphql/userQueries";
import {useOrgSettings} from "../../context/OrgSettingsProvider";
import {usePermissions} from "../../context/AuthzProvider";
import {useTopbarStyles} from "./topbarStyles";
import {useTour} from "../tour/Context/TourProvider";
import {useUser} from "../user/Context/UserProvider";
import {useSidebarItemStyles} from "../sidebar/sidebarStyles";
import {useSidebarLayout} from "../sidebar/Context/SidebarProvider";
import SidebarIcon from "../sidebar/SidebarIcon";
import StyledTooltip from "../tooltips/StyledTooltip";
import {filter, isEmpty, reduce, sortBy} from "underscore";

const GettingStarted = memo(({popperPlacement = "bottom", collapsedIcon}) => {
  const orgSettings = useOrgSettings();
  const {classes} = useTopbarStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [tourToLoad, setTourToLoad] = useState(null);
  const [startItems, setStartItems] = useState([]);
  const navigate = useNavigate();
  const {setTourId, tourId} = useTour();
  const {user} = useUser();
  const {canManageNotification, canManageObligation, canManageOrganization, canManageTask} =
    usePermissions();
  const [saveMetadata] = useMutation(UPDATE_USER_METADATA);
  const inSidebar = collapsedIcon != null;
  const {isSidebarExpanded, isSidebarTextVisible} = useSidebarLayout();
  const {classes: itemClasses} = useSidebarItemStyles({
    isCurrentPage: false,
    inOverlay: inSidebar ? isSidebarTextVisible : false,
  });


  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  const handleClose = (event) => {
    if (anchorEl.current && anchorEl.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const launchTour = (url, nextTourId) => {
    if (tourId !== null) {
      setTourId(null);
    }

    navigate(url, {
      state: {
        startTour: true,
        tourId: nextTourId,
      },
    });
    setOpen(false);
  };

  useEffect(() => {
    if (tourId !== null || tourToLoad === null) {
      return;
    }
    setTourId(tourToLoad);
    setOpen(false);
    setTourToLoad(null);
  }, [tourId, tourToLoad]);

  const tourSectionComplete = useCallback(
    (tourId, section) => {
      const storage = user?.metadata?.tours;

      if (storage) {
        const completedProfileSections = storage?.[tourId]?.completedSections;
        if (completedProfileSections || !isEmpty(completedProfileSections)) {
          return completedProfileSections.includes(section);
        }
      }

      return false;
    },
    [user?.metadata?.tours],
  );

  const startItemsConfig = useMemo(
    () => [
      {
        id: 1,
        completed: tourSectionComplete("profile", "profile"),
        title: "Configure User Profile",
        details: "Customize your BreachRx experience",
        url: "/userProfile",
        tourId: "profile",
        duration: {minutes: 5},
        authorized: true,
      },
      {
        id: 2,
        completed: tourSectionComplete("orgSettings", "securityTour"),
        settingRequired: "orgFullname",
        title: "Customize Organization Settings",
        details: "Tailor BreachRx to your organization",
        url: "/organization/general",
        tourId: "orgSettings",
        duration: {minutes: 7},
        authorized: canManageOrganization,
      },
      {
        id: 3,
        completed: tourSectionComplete("compliance", "complianceTour"),
        title: "Select Compliance Frameworks",
        details: "Align to external and voluntary compliance frameworks",
        url: "/organization/compliance",
        tourId: "compliance",
        duration: {minutes: 8},
        authorized: canManageOrganization,
      },
      {
        id: 4,
        completed: tourSectionComplete("attributes", "attributesTour"),
        title: "Set Default Workflow Settings",
        details: "Preselect attributes and workflow settings for your organization",
        url: "/organization/attributes",
        tourId: "attributes",
        duration: {minutes: 10},
        authorized: canManageOrganization,
      },
      {
        id: 6,
        completed: tourSectionComplete("notifications", "notificationsTour"),
        title: "Customize Notification Settings",
        details: "Configure when and how users receieve notifications",
        url: "/notifications",
        tourId: "notifications",
        duration: {minutes: 5},
        authorized: canManageNotification,
      },
      {
        id: 5,
        completed: tourSectionComplete("obligation", "obligationTour"),
        title: "Update Local Law Enforcement Procedure",
        details: "Update the contact information for the local law enforcement procedure",
        url: "/procedures/list",
        tourId: "obligation",
        duration: {minutes: 8},
        authorized: canManageObligation && canManageTask,
      },
    ],
    [
      tourSectionComplete,
      canManageNotification,
      canManageObligation,
      canManageOrganization,
      canManageTask,
    ],
  );

  useEffect(() => {
    if (orgSettings?.settingsLoading) {
      return;
    }
    const items = [];

    for (const config of startItemsConfig) {
      if (config.settingRequired && config.completed) {
        const setting = orgSettings?.[config.settingRequired];
        if (isEmpty(setting)) {
          items.push({...config, completed: false});
          continue;
        }
      }

      items.push({...config});
    }

    setStartItems(items);
  }, [orgSettings?.settingsLoading, startItemsConfig]);

  const filteredItems = useMemo(
    () => filter(startItems, ({authorized}) => authorized),
    [startItems],
  );

  const completedPercentage = useMemo(
    () =>
      Math.floor(
        (filter(filteredItems, (item) => item.completed).length / filteredItems.length) * 100,
      ),
    [filteredItems],
  );

  const estimatedTime = useMemo(() => {
    const totalTime = reduce(
      filteredItems,
      (memo, step) => {
        return !step.completed ? memo.add(dayjs.duration(step.duration)) : memo;
      },
      dayjs.duration(0),
    );

    return totalTime.asSeconds() === 0 ? null : totalTime.humanize();
  }, [filteredItems]);

  const completed = filter(filteredItems, (item) => item.completed).length;
  const total = filteredItems.length;

  const dismissAll = () => {
    const metadata = user?.metadata ?? {};
    const tourStorage = metadata?.tours ?? {};
    saveMetadata({
      variables: {
        id: user?.id,
        metadata: {...metadata, tours: {...tourStorage, gettingStartedDismissed: true}},
      },
    });
    setOpen(false);
  };

  if (orgSettings?.settingsLoading) {
    return null;
  }
  if (user?.metadata?.tours?.gettingStartedDismissed || completed === total) {
    return (
      <span id="getting-started" data-testid="getting-started-complete" style={{display: "none"}} />
    );
  }

  const sortedItems = sortBy(filteredItems, (item) => Number(item.completed));

  const listItems = sortedItems.map((item) => (
    <ListItem
      key={item.id}
      secondaryAction={(
        <Icon edge="end" aria-label="completed">
          {item.completed ?
            (
              <CheckCircleIcon color="success" />
            ) :
            (
              <RadioButtonUncheckedOutlinedIcon />
            )}
        </Icon>
      )}
    >
      <ListItemButton
        className={classes.startedItemButton}
        onClick={() => launchTour(item.url, item.tourId)}
      >
        <ArrowForwardIosIcon fontSize="small" sx={{paddingRight: 1}} />
        <ListItemText
          primary={(
            <>
              <Typography
                display="inline"
                className={item.completed ? classes.completedItem : classes.incompleteItem}
              >
                {item.title}
              </Typography>
              {!item.completed && (
                <Typography variant="caption">
                  {" "}
                  -
                  {" "}
                  <i>{dayjs.duration(item.duration).humanize()}</i>
                </Typography>
              )}
            </>
          )}
          secondary={(
            <Typography
              variant="caption"
              className={item.completed ? classes.completedItemCaption : ""}
            >
              {item.details}
            </Typography>
          )}
        />
      </ListItemButton>
    </ListItem>
  ));

  const sidebarButton = (
    <ListItemButton
      onClick={handleClick}
      className={itemClasses.item}
      id="getting-started"
      aria-haspopup="true"
      aria-expanded={open}
      disableRipple
    >
      <SidebarIcon icon={collapsedIcon} />
      <ListItemText
        primary={(
          <>
            Getting Started
            <LinearProgress
              variant="determinate"
              value={completedPercentage}
              className={classes.progressBar}
            />
          </>
        )}
        className={isSidebarTextVisible ? itemClasses.overlayItemText : itemClasses.itemText}
        primaryTypographyProps={{variant: "body2", component: "div"}}
      />
    </ListItemButton>
  );

  return (
    <>
      {inSidebar ?
        (
          isSidebarExpanded ?
            (
              sidebarButton
            ) :
            (
              <StyledTooltip title="Getting Started" placement="right">
                {sidebarButton}
              </StyledTooltip>
            )
        ) :
        (
          <Button
            onClick={handleClick}
            variant="outlined"
            color="grey"
            className={classes.gettingStartedButton}
            id="getting-started"
          >
            <Typography variant="caption" className={classes.gettingStartedText}>
              Getting Started
              {" "}
              {" | "}
              {completedPercentage}
              %
            </Typography>
            <Box sx={{width: "100%"}}>
              <LinearProgress
                variant="determinate"
                value={completedPercentage}
                className={classes.progressBar}
              />
            </Box>
          </Button>
        )}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={popperPlacement}
        transition
        sx={{zIndex: 1500, pt: 1}}
      >
        {({TransitionProps}) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper className={classes.gettingStartedPaper}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box className={classes.startedContainer}>
                  <Box className={classes.startedHeader}>
                    <Box>
                      <Typography variant="body1" className={classes.startedTitle}>
                        Getting Started with BreachRx®
                      </Typography>
                      <Typography variant="caption">
                        {completed}
                        {" "}
                        of
                        {total}
                        {" "}
                        complete
                        {estimatedTime && ` (about ${estimatedTime} total left)`}
                      </Typography>
                    </Box>
                    <StyledTooltip title="Dismiss Getting Started Guides" placement="top">
                      <IconButton
                        size="small"
                        onClick={dismissAll}
                        aria-label="Dismiss all tasks"
                        className={classes.dismissButton}
                        id="dismiss-getting-started"
                      >
                        <DoneAllIcon fontSize="small" />
                      </IconButton>
                    </StyledTooltip>
                  </Box>
                  <div className={classes.startedListWrapper}>
                    <List dense>{listItems}</List>
                  </div>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
});

GettingStarted.displayName = "GettingStarted";

export default GettingStarted;
